import { useT } from '@/shared/i18n'
import { ForbiddenError } from '@/shared/auth'
import { attemptChunkRecovery, isChunkLoadError } from '@/shared/lib/chunk-recovery'
import { Button, ErrorState, LoadingState } from '@/shared/ui'

export interface RouteErrorPageProps {
  error: unknown
  reset: () => void
}

/** Root error boundary content: forbidden, stale-deployment chunk errors, and unexpected errors. */
export function RouteErrorPage({ error, reset }: RouteErrorPageProps) {
  const t = useT()

  if (error instanceof ForbiddenError) {
    return (
      <main className="p-6">
        <ErrorState
          title={t('errors.forbiddenTitle')}
          description={t('errors.forbiddenDescription')}
        />
      </main>
    )
  }

  if (isChunkLoadError(error)) return <ChunkErrorState error={error} />

  return (
    <main className="p-6">
      <ErrorState onRetry={reset} />
    </main>
  )
}

/**
 * One recovery decision per error object, so re-renders (and StrictMode double renders) do not
 * trigger a second reload attempt or flip the decision.
 */
const recoveryByError = new WeakMap<object, 'reloading' | 'exhausted'>()

function recoveryFor(error: Error): 'reloading' | 'exhausted' {
  let decision = recoveryByError.get(error)
  if (!decision) {
    decision = attemptChunkRecovery()
    recoveryByError.set(error, decision)
  }
  return decision
}

function ChunkErrorState({ error }: { error: Error }) {
  const t = useT()
  const status = recoveryFor(error)

  if (status !== 'exhausted')
    return <LoadingState label={t('errors.reloading')} className="min-h-[60vh]" />

  return (
    <main className="p-6">
      <ErrorState title={t('errors.chunkTitle')} description={t('errors.chunkDescription')}>
        <Button
          onClick={() => {
            window.location.reload()
          }}
        >
          {t('actions.reload')}
        </Button>
      </ErrorState>
    </main>
  )
}
